import React, { useState, useEffect, useCallback } from 'react';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { openFirstRound, startNewCycle } from '../../cycle-engine';
import { AGREEMENT_VERSION, AGREEMENT_PLAIN_TEXT } from '../../onboarding/constants/agreementTerms';
import { sha256Hex } from '../../../shared/utils/sha256';

// Consent-status pill (Stage 22). Mirrors get_group_consent_status states.
const CONSENT_BADGE = {
  self_signed:    { label: 'Signed',        cls: 'bg-success-100 text-success-800' },
  admin_attested: { label: 'Admin-attested', cls: 'bg-warning-100 text-warning-800' },
  not_consented:  { label: 'No consent',    cls: 'bg-error-100 text-error-800' },
};

// ============================================================================
// StartCycleFlow — the ONE flow that opens a cycle (cycle 1 via mode="first",
// cycle 2+ via mode="new"). Roster reconciliation -> rotation order -> schedule
// -> confirm summary -> fire. On fire it calls the frozen engine RPC
// (open_first_round / start_new_cycle), which stamps each round's dates.
//
// ⚠️ ALL user-facing copy in this file is PROVISIONAL — flagged for a copy/flow
// review before the bundled go-live. Search "COPY:" for every string.
// ============================================================================

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },        // COPY: frequency labels
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
];

// Client-side PREVIEW of the server's date stamping (calendar-correct monthly,
// same day-of-month clamped to month length). The server is the source of truth;
// this only powers the confirm-summary preview.
function previewDate(firstDate, frequency, roundIndex /* 0-based */) {
  if (!firstDate) return null;
  const [y, m, d] = firstDate.split('-').map(Number);
  if (frequency === 'monthly') {
    const target = (m - 1) + roundIndex;
    const ty = y + Math.floor(target / 12);
    const tm = ((target % 12) + 12) % 12;
    const lastDay = new Date(ty, tm + 1, 0).getDate();
    return new Date(ty, tm, Math.min(d, lastDay));
  }
  const step = frequency === 'bi-weekly' ? 14 : 7;
  return new Date(y, m - 1, d + roundIndex * step);
}
const fmt = (dt) => (dt ? dt.toLocaleDateString() : '—');
// Allow a same-day first collection (min = today), so a cycle can be started to
// match a real group whose first collection is due today. Engine stamps round 1 =
// this date, subsequent rounds at the frequency offset.
// LOCAL date (not toISOString/UTC): an evening user in a negative-UTC zone is
// already "tomorrow" in UTC, which would wrongly floor the picker a day ahead.
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Fisher-Yates — a fair random draw for the "Shuffle" auto-assign (common in susus).
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const StartCycleFlow = ({ groupId, mode = 'first', onClose, onDone }) => {
  const [step, setStep] = useState(1);
  const [group, setGroup] = useState(null);
  const [hands, setHands] = useState([]);
  const [members, setMembers] = useState([]);
  const [order, setOrder] = useState([]);            // ordered ACTIVE hand ids (downstream contract)
  const [posDraft, setPosDraft] = useState({});      // Order step working map: handId -> position (1..N) or ''
  const [frequency, setFrequency] = useState('weekly');
  const [firstDate, setFirstDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Stage 22 consent gate: per-member status + the attestation dialog.
  const [consentStatus, setConsentStatus] = useState({});   // member_db_id -> row
  const [consentLoading, setConsentLoading] = useState(true);
  const [attestFor, setAttestFor] = useState(null);         // { member_db_id, name } being attested
  const [attestAffirm, setAttestAffirm] = useState(false);
  const [attestNote, setAttestNote] = useState('');
  const [attesting, setAttesting] = useState(false);
  const [attestError, setAttestError] = useState(null);

  const gid = parseInt(groupId, 10);

  const fetchConsent = useCallback(async () => {
    setConsentLoading(true);
    try {
      const { data } = await supabaseClient.rpc('get_group_consent_status', { p_group_id: gid });
      setConsentStatus(Object.fromEntries((data || []).map(r => [String(r.member_db_id), r])));
    } finally {
      setConsentLoading(false);
    }
  }, [gid]);

  useEffect(() => { fetchConsent(); }, [fetchConsent]);

  useEffect(() => {
    (async () => {
      const { data: g } = await supabaseClient.from('groups').select('*').eq('id', gid).single();
      const { data: h } = await supabaseClient.from('hands').select('*').eq('group_id', gid).order('position_number');
      const { data: m } = await supabaseClient.from('members').select('id, name').eq('group_id', gid);
      setGroup(g); setHands(h || []); setMembers(m || []);
      setFrequency(g?.contribution_frequency || 'weekly');
      setFirstDate(g?.start_date || today());
      setOrder((h || []).filter(x => x.is_active !== false).map(x => x.id));
      setLoading(false);
    })();
  }, [gid]);

  const isActive = (hand) => hand.is_active !== false;
  const activeHands = hands.filter(isActive);
  // Distinct active MEMBERS (a member can hold multiple hands, so this is not
  // activeHands.length). Rounds/pot stay hand-based — this is display only.
  const activeMemberCount = new Set(activeHands.map(h => h.member_db_id)).size;
  const memberName = (hand) => members.find(m => String(m.id) === String(hand?.member_db_id))?.name || 'Unassigned';
  const contribution = Number(group?.contribution_amount_per_hand || 0);
  const potSize = contribution * activeHands.length;
  const orderedActive = order.map(id => hands.find(h => String(h.id) === String(id))).filter(Boolean);

  // Consent gate (Stage 22). A member "has consent" per get_group_consent_status.
  const consentOf = (hand) => consentStatus[String(hand?.member_db_id)]?.consent_status;
  const activeMemberIds = [...new Set(activeHands.map(h => String(h.member_db_id)))];
  const unconsentedMembers = activeMemberIds
    .filter(id => consentStatus[id]?.consent_status === 'not_consented')
    .map(id => ({ member_db_id: Number(id), name: consentStatus[id]?.name || 'Member' }));
  const consentBlocked = !consentLoading && unconsentedMembers.length > 0;

  // Attest on a member's behalf (admin_attest_consent) — affirm-required, audit-logged.
  const doAttest = async () => {
    if (!attestFor || !attestAffirm) return;
    setAttesting(true); setAttestError(null);
    try {
      const sha = await sha256Hex(AGREEMENT_PLAIN_TEXT);
      const { error: attErr } = await supabaseClient.rpc('admin_attest_consent', {
        p_member_id: attestFor.member_db_id,
        p_terms_version: AGREEMENT_VERSION,
        p_terms_sha256: sha,
        p_affirm: true,
        p_note: attestNote.trim() || null,
      });
      if (attErr) throw attErr;
      setAttestFor(null); setAttestAffirm(false); setAttestNote('');
      await fetchConsent();
    } catch (e) {
      setAttestError(e.message || 'Could not record the attestation. Please try again.');
    } finally {
      setAttesting(false);
    }
  };
  const openAttest = (hand) => {
    setAttestError(null); setAttestAffirm(false); setAttestNote('');
    setAttestFor({ member_db_id: hand.member_db_id, name: memberName(hand) });
  };

  const toggleActive = async (hand) => {
    const next = !isActive(hand);
    setHands(prev => prev.map(h => (h.id === hand.id ? { ...h, is_active: next } : h)));
    setOrder(prev => (next ? [...prev, hand.id] : prev.filter(id => id !== hand.id)));
    try {
      await supabaseClient.from('hands').update({ is_active: next }).eq('id', hand.id);
    } catch {
      // revert on failure
      setHands(prev => prev.map(h => (h.id === hand.id ? { ...h, is_active: !next } : h)));
      setOrder(prev => (next ? prev.filter(id => id !== hand.id) : [...prev, hand.id]));
    }
  };

  // Seed the Order step's position map from the committed `order` (position = index+1),
  // then open it. Called on entry from both directions (Roster→Order, Schedule→Order)
  // so a returning admin sees their last valid assignment, not a reset.
  const enterOrderStep = () => {
    setPosDraft(Object.fromEntries(order.map((id, i) => [id, i + 1])));
    setStep(2);
  };

  // Direct position pick for one member. '' clears the assignment (flagged as a gap).
  const setPos = (handId, val) =>
    setPosDraft(prev => ({ ...prev, [handId]: val === '' ? '' : Number(val) }));

  // One-click valid permutation. 'sequential' keeps the current row order; 'shuffle'
  // draws a random fair order. Either way the admin can still tweak dropdowns after.
  const autoAssign = (variant) => {
    const ids = orderedActive.map(h => h.id);
    const seq = variant === 'shuffle' ? shuffle(ids) : ids;
    setPosDraft(Object.fromEntries(seq.map((id, i) => [id, i + 1])));
  };

  // Commit the (validated) permutation back to the downstream `order` array, then advance.
  // Only reachable when the draft is a valid 1..N permutation, so `order` stays a clean
  // ordered hand-id list — the exact shape start_new_cycle / open_first_round expect.
  const commitOrderAndNext = () => {
    const ordered = activeHands.slice()
      .sort((a, b) => Number(posDraft[a.id]) - Number(posDraft[b.id]))
      .map(h => h.id);
    setOrder(ordered);
    setStep(3);
  };

  const handleFire = async () => {
    setSubmitting(true); setError(null);
    try {
      const args = { collectorOrder: order, firstCollectionDate: firstDate, frequency };
      if (mode === 'new') await startNewCycle(gid, args);
      else await openFirstRound(gid, args);
      onDone?.();
    } catch (e) {
      setError(e.message || 'Could not start the cycle. Please try again.');  // COPY
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedRoster = activeHands.length >= 2 && !consentBlocked && !consentLoading;

  if (loading) return <Modal onClose={onClose}><p className="text-center py-6">Loading…</p></Modal>;

  return (
    <>
    <Modal onClose={onClose}>
      {/* Header + step indicator */}
      <div className="flex justify-between items-center mb-1">
        {/* COPY: titles */}
        <h2 className="text-lg font-bold text-neutral-900">
          {mode === 'new' ? 'Start a New Cycle' : 'Start Cycle 1'}
        </h2>
        <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl">✕</button>
      </div>
      <p className="text-xs text-neutral-500 mb-4">Step {step} of 4 · {['Roster', 'Order', 'Schedule', 'Confirm'][step - 1]}</p>

      {/* STEP 1 — ROSTER */}
      {step === 1 && (
        <div>
          {/* COPY */}
          <p className="text-sm text-neutral-600 mb-3">Choose who’s in this cycle. Turn a member off to skip them this cycle (their history is kept). Every active member must have consented at the current agreement version.</p>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {hands.map(hand => {
              const cs = isActive(hand) ? consentOf(hand) : null;
              const badge = cs ? CONSENT_BADGE[cs] : null;
              return (
                <div key={hand.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-neutral-900 truncate">
                      <span className="text-neutral-500 mr-2">#{hand.position_number}</span>{memberName(hand)}
                    </p>
                    {isActive(hand) && (
                      <div className="mt-1 flex items-center gap-2">
                        {consentLoading ? (
                          <span className="text-xs text-neutral-400">checking consent…</span>
                        ) : badge ? (
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.cls}`}>{badge.label}</span>
                        ) : null}
                        {!consentLoading && cs === 'not_consented' && (
                          <button onClick={() => openAttest(hand)} className="text-xs font-medium text-primary-600 hover:text-primary-700 underline">
                            Attest on their behalf
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <input type="checkbox" checked={isActive(hand)} onChange={() => toggleActive(hand)} className="h-5 w-5 flex-shrink-0" />
                </div>
              );
            })}
          </div>

          {activeHands.length < 2 && (
            <p className="text-sm text-error-600 mt-3">A cycle needs at least 2 active members.</p>  /* COPY */
          )}
          {consentBlocked && (
            <div className="mt-3 rounded-lg border border-error-300 bg-error-50 p-3">
              <p className="text-sm font-semibold text-error-800">
                {unconsentedMembers.length === 1 ? '1 active member has not consented' : `${unconsentedMembers.length} active members have not consented`} — resolve before starting:
              </p>
              <p className="text-sm text-error-700 mt-1">{unconsentedMembers.map(m => m.name).join(', ')}</p>
              <p className="text-xs text-error-700 mt-2">
                For each: <strong>wait</strong> for them to sign, <strong>turn them off</strong> above to skip this cycle, or <strong>attest</strong> on their behalf (you confirm they agreed).
              </p>
            </div>
          )}
          <FooterNav
            onBack={onClose} backLabel="Cancel"
            onNext={enterOrderStep} nextLabel="Next: Order" nextDisabled={!canProceedRoster}
          />
        </div>
      )}

      {/* STEP 2 — ORDER (per-member position dropdowns + live validation) */}
      {step === 2 && (() => {
        const N = activeHands.length;
        // Count each chosen position to find duplicates; build the set of assigned
        // positions to find gaps. Positions come only from the 1..N dropdowns.
        const countByPos = {};
        activeHands.forEach(h => { const p = posDraft[h.id]; if (p) countByPos[p] = (countByPos[p] || 0) + 1; });
        const dupPositions = new Set(Object.keys(countByPos).filter(p => countByPos[p] > 1).map(Number));
        const assigned = new Set(activeHands.map(h => posDraft[h.id]).filter(Boolean).map(Number));
        const missing = [];
        for (let p = 1; p <= N; p++) if (!assigned.has(p)) missing.push(p);
        const isValid = dupPositions.size === 0 && missing.length === 0;   // ⟺ a valid 1..N permutation

        const issues = [];
        [...dupPositions].sort((a, b) => a - b).forEach(p => {             // COPY: validation messages
          const n = countByPos[p];
          issues.push(`Position ${p} assigned ${n === 2 ? 'twice' : `${n} times`}`);
        });
        missing.forEach(p => issues.push(`Position ${p} unassigned`));

        return (
          <div>
            <p className="text-sm text-neutral-600 mb-3">Set each member’s collection position — position 1 collects first.</p>{/* COPY */}
            <div className="flex gap-2 mb-3">
              <button onClick={() => autoAssign('sequential')}
                className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-700">Auto-assign</button>{/* COPY */}
              <button onClick={() => autoAssign('shuffle')}
                className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg text-sm text-neutral-700">Shuffle</button>{/* COPY */}
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {orderedActive.map(hand => {
                const v = posDraft[hand.id];
                const isDup   = v && dupPositions.has(Number(v));
                const isBlank = !v;
                const rowCls = isDup ? 'border border-error-400 bg-error-50'
                  : isBlank ? 'border border-warning-400 bg-warning-50'
                  : 'bg-neutral-50';
                return (
                  <div key={hand.id} className={`flex items-center justify-between p-3 rounded-lg ${rowCls}`}>
                    <span className="text-sm text-neutral-900 truncate mr-2">{memberName(hand)}</span>
                    <select
                      value={v ?? ''}
                      onChange={e => setPos(hand.id, e.target.value)}
                      className={`border rounded-lg px-2 py-1.5 text-sm flex-shrink-0 ${isDup ? 'border-error-400 text-error-700' : isBlank ? 'border-warning-400 text-warning-700' : 'border-neutral-300'}`}
                    >
                      <option value="">—</option>
                      {Array.from({ length: N }, (_, i) => i + 1).map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
            {isValid ? (
              <p className="mt-3 text-sm text-success-700">All positions assigned — ready.</p>  /* COPY */
            ) : (
              <div className="mt-3 text-sm text-error-600 space-y-0.5">
                {issues.map((t, i) => <p key={i}>{t}</p>)}
              </div>
            )}
            <FooterNav onBack={() => setStep(1)} onNext={commitOrderAndNext} nextLabel="Next: Schedule" nextDisabled={!isValid} />
          </div>
        );
      })()}

      {/* STEP 3 — SCHEDULE */}
      {step === 3 && (
        <div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1">Frequency</label>{/* COPY */}
            <select value={frequency} onChange={e => setFrequency(e.target.value)} className="w-full border border-neutral-300 rounded-lg px-3 py-2">
              {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div className="mb-2">
            <label className="block text-sm font-medium text-neutral-700 mb-1">First collection date</label>{/* COPY */}
            <input type="date" value={firstDate} min={today()} onChange={e => setFirstDate(e.target.value)} className="w-full border border-neutral-300 rounded-lg px-3 py-2" />
          </div>
          <p className="text-xs text-neutral-500">This anchors the schedule; each later collection steps forward by the frequency.</p>{/* COPY */}
          <FooterNav onBack={enterOrderStep} onNext={() => setStep(4)} nextLabel="Review" nextDisabled={!firstDate} />
        </div>
      )}

      {/* STEP 4 — CONFIRM SUMMARY (safety-critical) */}
      {step === 4 && (
        <div>
          <p className="text-sm text-neutral-600 mb-3">Review carefully — starting the cycle creates each member’s obligations.</p>{/* COPY */}

          <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
            <Stat label="Members" value={activeMemberCount} />                        {/* COPY: stat labels */}
            <Stat label="Hands" value={activeHands.length} />
            <Stat label="Rounds" value={activeHands.length} />
            <Stat label="Contribution / round" value={`$${contribution.toFixed(2)}`} />
            <Stat label="Pot / round" value={`$${potSize.toFixed(2)}`} />
            <Stat label="Frequency" value={FREQUENCIES.find(f => f.value === frequency)?.label || frequency} />
            <Stat label="First collection" value={fmt(previewDate(firstDate, frequency, 0))} />
          </div>

          <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">Collection order &amp; dates</p>{/* COPY */}
          <div className="space-y-1 max-h-56 overflow-y-auto mb-2">
            {orderedActive.map((hand, idx) => (
              <div key={hand.id} className="flex justify-between items-center text-sm bg-neutral-50 rounded-lg px-3 py-2">
                <span className="text-neutral-900"><span className="text-neutral-500 mr-2">{idx + 1}.</span>{memberName(hand)}</span>
                <span className="text-neutral-600">{fmt(previewDate(firstDate, frequency, idx))}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-neutral-500 mb-4">Everyone contributes ${contribution.toFixed(2)} each round; that round’s collector receives the full pot.</p>{/* COPY */}

          {error && <p className="text-sm text-error-600 mb-3">{error}</p>}
          <FooterNav
            onBack={() => setStep(3)}
            onNext={handleFire}
            nextLabel={submitting ? 'Starting…' : (mode === 'new' ? 'Start New Cycle' : 'Start Cycle')}  /* COPY */
            nextDisabled={submitting}
          />
        </div>
      )}
    </Modal>

    {/* Admin-attest affirmation dialog (Stage 22, option c) — deliberate,
        audit-logged, flagged. Layered over the wizard. COPY: provisional. */}
    {attestFor && (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end sm:items-center justify-center z-[60] p-4" onClick={() => setAttestFor(null)}>
        <div className="bg-neutral-100 rounded-2xl sm:rounded-lg p-6 w-full sm:max-w-md" onClick={e => e.stopPropagation()}>
          <h3 className="text-lg font-bold text-neutral-900 mb-1">Record consent on their behalf</h3>
          <p className="text-sm text-neutral-600 mb-4">
            For <strong>{attestFor.name}</strong>. Use this only when the member has agreed to the terms but has not signed in the app yet. It is recorded as an <strong>admin attestation</strong> (distinct from a member’s own signature), logged and flagged for review.
          </p>
          <label className="flex items-start space-x-2 cursor-pointer mb-3">
            <input type="checkbox" checked={attestAffirm} onChange={e => setAttestAffirm(e.target.checked)} className="mt-0.5" />
            <span className="text-sm text-neutral-800">
              I confirm {attestFor.name} agreed to the current Contribution Agreement ({AGREEMENT_VERSION}), and I am recording this consent on their behalf.
            </span>
          </label>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Note (optional)</label>
          <input
            type="text" value={attestNote} onChange={e => setAttestNote(e.target.value)}
            placeholder="e.g. agreed verbally at the 8/3 meeting"
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm mb-3"
          />
          {attestError && <p className="text-sm text-error-600 mb-3">{attestError}</p>}
          <div className="flex gap-2">
            <button onClick={() => setAttestFor(null)} disabled={attesting} className="flex-1 px-4 py-3 border border-neutral-300 rounded-lg text-base text-neutral-700">Cancel</button>
            <button onClick={doAttest} disabled={!attestAffirm || attesting} className="flex-1 px-4 py-3 bg-warning-600 text-white rounded-lg text-base font-medium disabled:opacity-50">
              {attesting ? 'Recording…' : 'Record attestation'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

// --- small presentational helpers (mobile-first modal shell) ---
const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50" onClick={onClose}>
    <div className="bg-neutral-100 rounded-t-2xl sm:rounded-lg p-6 w-full sm:max-w-md max-h-screen overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="w-10 h-1 bg-neutral-300 rounded-full mx-auto mb-4 sm:hidden" />
      {children}
    </div>
  </div>
);
const Stat = ({ label, value }) => (
  <div className="bg-neutral-50 rounded-lg px-3 py-2">
    <p className="text-xs text-neutral-500">{label}</p>
    <p className="text-base font-semibold text-neutral-900">{value}</p>
  </div>
);
const FooterNav = ({ onBack, backLabel = 'Back', onNext, nextLabel, nextDisabled }) => (
  <div className="flex gap-2 mt-6">
    <button onClick={onBack} className="flex-1 px-4 py-3 border border-neutral-300 rounded-lg text-base text-neutral-700">{backLabel}</button>
    <button onClick={onNext} disabled={nextDisabled} className="flex-1 px-4 py-3 bg-primary-600 text-neutral-50 rounded-lg text-base font-medium disabled:opacity-50">{nextLabel}</button>
  </div>
);

export default StartCycleFlow;
